/**
 * Utility hooks for filtering, sorting, and paginating arrays
 * and keeping the state in sync with the URL
 */

import { useState, useMemo } from "react";
import { getNestedValueFromString, useEffectAfterMount } from "./utils";

// Add this utility function to get query params from URL
export const getQueryParamsFromURL = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const params = {};
  searchParams.forEach((value, key) => {
    params[key] = value;
  });
  return params;
};

export type SortOrder = "asc" | "desc";

export type Sort = {
  by: string,
  order: SortOrder
};

export type Paging = {
  current: number,
  perPage: number
};

export const useQueryParams = (paramsList): [Record<string, any>, (newParams: Record<string, any>) => void] => {
  let initialParams = {};

  for (const key in paramsList) {
    if (paramsList[key] && paramsList[key].default) {
      if (typeof paramsList[key].default === "string") {
        switch (paramsList[key].type) {
          case "datetime":
            initialParams[key] = new Date(paramsList[key].default);
            break;
          case "object":
          case "array":
            initialParams[key] = JSON.parse(paramsList[key].default);
            break;
          default:
            initialParams[key] = paramsList[key].default;
        }
      } else {
        initialParams[key] = paramsList[key].default;
      }
    }
  }

  // Replace Rails context with URL params
  const query_params = getQueryParamsFromURL();
  for (const key in query_params) {
    if (paramsList[key]) {
      switch (paramsList[key].type) {
        case "number":
          initialParams[key] = parseFloat(query_params[key]);
          break;
        case "datetime":
          initialParams[key] = new Date(query_params[key]);
          break;
        case "object":
        case "array":
          initialParams[key] = JSON.parse(query_params[key]);
          break;
        default:
          initialParams[key] = query_params[key];
      }
    }
  }

  const [ params, setParams ] = useState(initialParams);
  const setSomeParams = (newParams) => {
    setParams({ ...params, ...newParams });
  };

  useEffectAfterMount(() => {
    const queryParams = new URLSearchParams();
    for (const key in params) {
      if (params[key] !== undefined && params[key] !== null && params[key].toString().trim() !== "" && paramsList[key] !== undefined) {
        switch (paramsList[key].type) {
          case "datetime":
            queryParams.set(key, params[key].toISOString());
            break;
          case "object":
          case "array":
            queryParams.set(key, JSON.stringify(params[key]));
            break;
          default:
            queryParams.set(key, params[key]);
        }
      }
    }

    let queryString = queryParams.toString();
    queryString = queryString ? `?${queryString}` : "";

    window.history.replaceState(null, "", `${window.location.pathname}${queryString}${window.location.hash}`);
  }, [ params ]);

  return [ params, setSomeParams ];
};

/**
 * Hook to automatically reset page to 1 when filters or date range changes
 * Usage:
 *   - useResetPageOnFilterChange(params, setParams) // Default: watches filters, start, end
 *   - useResetPageOnFilterChange(params, setParams, [params.filters]) // Custom: only watches filters
 *   - useResetPageOnFilterChange(params, setParams, [params.filters, params.status]) // Custom: watches filters and status
 */
export const useResetPageOnFilterChange = (
  params: Record<string, any>,
  setParams: (newParams: Record<string, any>) => void,
  dependencies?: any[]
) => {
  // Use default dependencies if not provided
  const deps = dependencies || [ params.filters, params.start, params.end ];

  // Serialize dependencies to strings for stable comparison
  // This handles arrays and objects properly
  const serializedDeps = useMemo(() =>
    deps.map(dep => {
      if (dep === undefined || dep === null) return "undefined";
      if (typeof dep === "object") return JSON.stringify(dep);
      return String(dep);
    }),
  deps
  );

  useEffectAfterMount(() => {
    setParams({ page: 1 });
  }, serializedDeps);
};

/**
 * This effect will save the current sortBy, sortOrder, currentPage, and pageSize to the URL
 * so that the user can share the current view via a link
 */
export const useSaveParamsInURLEffect = ({ sort, paging, filters, range }) => {
  const { by: sortBy, order: sortOrder } = sort;
  const { current: currentPage, perPage: pageSize } = paging;
  const { start, end } = range;
  // console.warn("useSaveParamsInURLEffect", sortBy, sortOrder, currentPage, pageSize);

  // Doesn't run on first render
  // Only change the URL if the user has interacted with the table
  useEffectAfterMount(() => {
    const params = new URLSearchParams();
    if (sortBy) params.set("sort_by", sortBy);
    if (sortOrder) params.set("sort_order", sortOrder);
    if (currentPage) params.set("page", currentPage.toString());
    if (pageSize) params.set("per_page", pageSize.toString());
    if (filters && filters.length > 0) params.set("filters", JSON.stringify(filters));
    if (start && end) {
      params.set("start", start.toISOString());
      params.set("end", end.toISOString());
    }

    const queryString = params.toString();
    if (queryString) {
      window.history.replaceState(null, "", `${window.location.pathname}?${queryString}${window.location.hash}`);
    }
  }, [ sortBy, sortOrder, currentPage, pageSize, filters, start, end ]);
};

/**
 * This hook will take an array and return a paged, sorted, and filtered array (filtering not yet implemented)
 * based on the current sortBy, sortOrder, currentPage, and pageSize
 */
export const useFilteredSortedPaginatedArray = ({
  array,
  sort,
  paging,
  sortFunctions = {},
}) => {
  const { by: sortBy, order: sortOrder } = sort;
  const { current: currentPage, perPage: pageSize } = paging;

  const visibleArray = useMemo(() => {
    // console.warn("sorting array", sortBy, sortOrder, array);
    if (!sortBy) return array;

    let sortByFn = sortBy;
    if (typeof sortByFn !== "function") {
      if (sortFunctions[sortBy]) {
        sortByFn = sortFunctions[sortBy];
      } else if (sortBy.includes(".")) {
        sortByFn = (row) => {
          return getNestedValueFromString(row, sortBy);
        };
      } else {
        sortByFn = (row) => row && row[sortBy];
      }
    }

    // Use the collator to sort strings case insensitive with international character support
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare#Performance
    const collator = new Intl.Collator();
    const sortedArray = [ ...array ].sort((a, b) => {

      const aRawValue = sortByFn(a);
      const bRawValue = sortByFn(b);
      const aValue = aRawValue !== null && aRawValue !== undefined ? aRawValue : "";
      const bValue = bRawValue !== null && bRawValue !== undefined ? bRawValue : "";
      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = collator.compare(aValue, bValue);
        if (comparison !== 0) {
          return sortOrder === "asc" ? comparison : -comparison;
        }
      }

      // Sort null/undefined values first using rawValues
      if (aRawValue === null || aRawValue === undefined) return sortOrder === "asc" ? -1 : 1;
      if (bRawValue === null || bRawValue === undefined) return sortOrder === "asc" ? 1 : -1;

      // If both rawValues are null/undefined, maintain their original order
      if ((aRawValue === null || aRawValue === undefined) && (bRawValue === null || bRawValue === undefined)) return 0;

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    const page = currentPage || 1;
    const perPage = pageSize || 100;

    const startIndex = (page - 1) * perPage;
    return sortedArray.slice(startIndex, startIndex + perPage);
  }, [ array, sortBy, sortOrder, sortFunctions, currentPage, pageSize ]);

  return visibleArray;
};
